import { useEffect, useRef, useState, type RefObject } from 'react';

import { useI18n } from '../i18n/LanguageProvider.js';

/**
 * A curated set rather than a full picker library. This is for finishing a
 * sentence someone is typing to a customer, not for browsing every emoji that
 * exists — a short, familiar row a person can scan in one glance.
 */
const EMOJIS = [
  '🙂', '😀', '😂', '😍', '😉', '😢', '😮', '😅', '🤔', '😴',
  '👍', '👎', '🙏', '👏', '🎉', '❤️', '🔥', '💯', '✅', '❌',
];

/**
 * A button beside a text field that opens a small emoji grid and inserts
 * whichever one is chosen at the cursor.
 *
 * Cursor-aware rather than append-only, because the common case here is
 * finishing or correcting a sentence already in the box, not always adding to
 * its end — a reply reworded after a suggestion was dropped in, say. Works
 * against any input or textarea the caller points it at, so the same button
 * serves the composer, the inbox reply box and anywhere else text is typed.
 */
export function EmojiPicker({
  target,
  value,
  onChange,
}: {
  /** The field this picker inserts into. */
  target: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  value: string;
  onChange: (next: string) => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function insert(emoji: string): void {
    const el = target.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + emoji + value.slice(end));
    setOpen(false);

    // Give the field its focus and caret back, so picking an emoji reads as
    // continuing to type rather than as leaving the field to go do something
    // else. Deferred a frame: the value above has to reach the field first.
    requestAnimationFrame(() => {
      const caret = start + emoji.length;
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  }

  return (
    <div className="emoji-picker" ref={container}>
      <button
        type="button"
        className="ghost small emoji-picker-toggle"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={t('emoji.title')}
        title={t('emoji.title')}
        onClick={() => setOpen((current) => !current)}
      >
        🙂
      </button>

      {open && (
        <div className="emoji-picker-panel" role="menu">
          {EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="emoji-picker-option"
              onClick={() => insert(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
